import {noop} from 'lodash';
import React, {FC, useContext, useEffect, useMemo} from 'react';
import styled from 'styled-components';

import {buildInteractionIdProps} from '../../../../libs/shared/helpers/src/interaction/interactionHelpers';
import {useInitialEffect} from '../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {PushContext} from '../../../../srcCommon/push/pushContext';
import {StyledStretchGridItem} from '../../../components/common/grid';
import {computeIsGlobalNamespace} from '../../../helpers/models/namespaceHelpers';
import {useAppDispatch, useAppSelector} from '../../../helpers/reduxHelpers';
import {InterfaceEventsEnum} from '../../../services/tracking';
import {
  getCompanyHasAccountsAnalytics,
  getCompanyHasCsatSurveysFeature,
} from '../../../store/data/company/companySelectors';
import {getHasAnalyticsResolutionMetrics} from '../../../store/featureFlags/featureFlagsSelectors';
import {isPusherRoomAnalytics} from '../../../store/panels/analytics/analyticsPanelHelpers';
import {
  AnalyticsPanelDetailsActionsEnum,
  fetchMetricDetailsAction,
  SetConversationsPageNumberAction,
} from '../../../store/panels/analytics/details/analyticsPanelDetailsActions';
import {getIsSidePanelOpen} from '../../../store/panels/analytics/details/analyticsPanelDetailsSelectors';
import {getAnalyticsFilters} from '../../../store/panels/analytics/filters/analyticsPanelFiltersSelectors';
import {
  getAnalyticsDatetimes,
  getAnalyticsReportType,
  getAnalyticsSelectedNamespace,
  getAnalyticsSelectedViewId,
} from '../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {onAnalyticsRoomSubscriptionAction} from '../../../store/panels/analytics/report/analyticsPanelReportActions';
import {
  getAnalyticsBucketBoundaries,
  getAnalyticsReportErrors,
  getAnalyticsReportIsLoading,
} from '../../../store/panels/analytics/report/analyticsPanelReportSelectors';
import {getAnalyticsSelectedView} from '../../../store/panels/analytics/views/analyticsPanelViewsSelectors';
import {isAnalyticsViewSaved} from '../../../store/panels/analytics/views/analyticsPanelViewsState';
import {
  AnalyticsReportsEnum,
  BasicReportProps,
  getAnalyticsReportConfig,
  ReportFilterOptionsEnum,
} from '../../helpers/analyticsReportHelpers';
import {useAnalyticsTracking} from '../../helpers/tracking/useAnalyticsTracking';
import {ReportErrorBanner} from '../reportElements/banners/reportErrorBanner';
import {ReportHeader} from '../reportHeader/reportHeader';
import {ReportSidePanel} from '../reportSidePanel/reportSidePanel';
import {ConversationsReport} from './conversationsReport/conversationsReport';
import {CustomerExperienceReport} from './customerExperienceReport/customerExperienceReport';
import {MessageTemplateReport} from './messageTemplateReport/messageTemplateReport';
import {OverviewReport} from './overviewReport/overviewReport';
import {reportInnerContent} from './reportCommons';
import {ReportGrid} from './reportGrid';
import {SlaReport} from './slaReport/slaReport';
import {TagsReport} from './tagsReport/tagsReport';
import {TeamPerformanceReport} from './teamPerformanceReport/teamPerformanceReport';
import {UpsellCardOverlay} from './upsell/upsellCardOverlay';

/*
 * Props.
 */

interface ReportViewerProps {
  isVisible: boolean;
}

/*
 * Style.
 */

const StyledReportViewerWrapper = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
`;

const StyledReportHeaderWrapper = styled.div`
  ${reportInnerContent}
  width: 100%;
  min-width: 0; /* Making sure tabs can shrink on smaller screens. */
`;

// Overflow wrapper and Viewer content are split into 2 to have the scrollbar to the right.
const StyledReportViewerOverflow = styled.div`
  overflow-y: auto;
  padding-bottom: 48px;
`;

const StyledReportViewerContent = styled(ReportGrid)`
  ${reportInnerContent}
  padding: 0 48px;
`;

const StyledErrorBanner = styled(ReportErrorBanner)`
  margin: 0 48px;
`;

const StyledRelativeStretchGridItem = styled(StyledStretchGridItem)`
  position: relative;
`;

/*
 * Component.
 */

export const ReportViewer: FC<ReportViewerProps> = ({isVisible}) => {
  const dispatch = useAppDispatch();
  const {roomSubscribes$} = useContext(PushContext);

  useInitialEffect(() => {
    // Subscribe to the pusher room subscriptions.
    if (roomSubscribes$) {
      const subscription = roomSubscribes$.subscribe((roomName) => {
        // If we subscribed to an analytics room, dispatch a poll request.
        if (isPusherRoomAnalytics(roomName)) {
          dispatch(onAnalyticsRoomSubscriptionAction(roomName));
        }
      });
      return () => subscription.unsubscribe(); // Unsubscribe on unmount.
    }

    return noop;
  });

  const isLoading = useAppSelector(getAnalyticsReportIsLoading);
  const reportErrors = useAppSelector(getAnalyticsReportErrors);
  const [startDate, endDate] = useAppSelector(getAnalyticsDatetimes);

  const namespace = useAppSelector(getAnalyticsSelectedNamespace);
  const reportType = useAppSelector(getAnalyticsReportType);
  const selectedView = useAppSelector(getAnalyticsSelectedView);
  const selectedViewId = useAppSelector(getAnalyticsSelectedViewId);
  const filters = useAppSelector(getAnalyticsFilters);
  const currentBucketBoundaries = useAppSelector(getAnalyticsBucketBoundaries);

  const hasCsatSurveysFeature = useAppSelector(getCompanyHasCsatSurveysFeature);

  const hasAccountsAnalyticsFeature = useAppSelector(getCompanyHasAccountsAnalytics);

  const hasResolutionMetrics = useAppSelector(getHasAnalyticsResolutionMetrics);

  const isSidePanelOpened = useAppSelector(getIsSidePanelOpen);

  const trackAnalytics = useAnalyticsTracking();

  const {title, options} = useMemo(
    () => getAnalyticsReportConfig(reportType, hasResolutionMetrics),
    [reportType, hasResolutionMetrics],
  );

  const defaultBucketBoundaries = options && options[ReportFilterOptionsEnum.BUCKET_BOUNDARIES];
  // Use the default values from the configs if the  bucket boundaries aren't set on the current report.
  const bucketBoundaries = {...defaultBucketBoundaries, ...currentBucketBoundaries};

  const startTime = startDate?.toMillis();
  const endTime = endDate?.toMillis();

  // Refresh the details panel if filters, view, namespace or date period changed.
  useEffect(() => {
    if (isSidePanelOpened) {
      // Reset the page number.
      // @TODO: Change this in a following PR.
      dispatch<SetConversationsPageNumberAction>({
        type: AnalyticsPanelDetailsActionsEnum.SET_CONVERSATIONS_PAGE_NUMBER,
        payload: 1,
      });
      dispatch(fetchMetricDetailsAction());
    }
  }, [namespace, selectedViewId, filters, startTime, endTime, dispatch, isSidePanelOpened]);

  useEffect(() => {
    trackAnalytics(InterfaceEventsEnum.ANALYTICS_REPORT_VIEW, {
      start_time: startTime,
      end_time: endTime,
    });
    // We only want to track when the reportType changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType]);

  useEffect(() => {
    trackAnalytics(InterfaceEventsEnum.ANALYTICS_VIEW_VIEW, {
      is_saved_view: isAnalyticsViewSaved(selectedViewId),
      is_default: selectedView?.isDefault ?? false,
      start_time: startTime,
      end_time: endTime,
    });
    // We only want to track when the selected view, report type, or namespace changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType, selectedViewId, namespace]);

  const reportProps: BasicReportProps = {
    isLoading,
  };

  const interactionId = 'analytics-overview-welcome';
  const interactionProps = buildInteractionIdProps(interactionId);

  return (
    <>
      <StyledRelativeStretchGridItem {...interactionProps} column={3}>
        <StyledReportViewerWrapper>
          <StyledReportHeaderWrapper>
            <ReportHeader title={title} />
            {maybeRenderErrors(reportErrors)}
          </StyledReportHeaderWrapper>
          <StyledReportViewerOverflow>
            <StyledReportViewerContent>
              {renderReport(
                reportType,
                reportProps,
                hasCsatSurveysFeature,
                hasAccountsAnalyticsFeature,
                namespace,
                bucketBoundaries,
              )}
            </StyledReportViewerContent>
          </StyledReportViewerOverflow>
        </StyledReportViewerWrapper>
        <UpsellCardOverlay reportType={reportType} />
      </StyledRelativeStretchGridItem>
      <ReportSidePanel isVisible={isVisible} />
    </>
  );
};

/*
 * Helpers.
 */

function renderReport(
  reportType: AnalyticsReportsEnum,
  props: BasicReportProps,
  hasCsatSurveysFeature: boolean,
  hasAccountsAnalyticsFeature: boolean,
  namespace: string,
  bucketBoundaries?: {[x: string]: ReadonlyArray<number>},
) {
  const {isLoading} = props;
  // If we aren't currently in the global namespace then every row in the tables will
  // all be from the same team - so we hide the colum in that case
  const isTeamColumnVisible = computeIsGlobalNamespace(namespace);

  switch (reportType) {
    case AnalyticsReportsEnum.OVERVIEW:
      return <OverviewReport isLoading={isLoading} bucketBoundaries={bucketBoundaries} />;

    case AnalyticsReportsEnum.TEAM_PERF:
      return <TeamPerformanceReport isLoading={isLoading} hasCsatSurveysFeature={hasCsatSurveysFeature} />;

    case AnalyticsReportsEnum.CONVERSATIONS:
      return <ConversationsReport isLoading={isLoading} isTeamColumnVisible={isTeamColumnVisible} />;

    case AnalyticsReportsEnum.TAGS:
      return <TagsReport isLoading={isLoading} isTeamColumnVisible={isTeamColumnVisible} />;

    case AnalyticsReportsEnum.MESSAGE_TEMPLATES:
      return <MessageTemplateReport isLoading={isLoading} isTeamColumnVisible={isTeamColumnVisible} />;

    case AnalyticsReportsEnum.CUSTOMER_EXPERIENCE:
      return (
        <CustomerExperienceReport
          isLoading={isLoading}
          hasAccountsAnalyticsFeature={hasAccountsAnalyticsFeature}
          hasCsatSurveysFeature={hasCsatSurveysFeature}
        />
      );

    case AnalyticsReportsEnum.SLA:
      return <SlaReport isLoading={isLoading} isTeamColumnVisible={isTeamColumnVisible} />;

    default:
      return null;
  }
}

function maybeRenderErrors(errors: Array<string>) {
  if (!errors || !errors.length) {
    return null;
  }

  return <StyledErrorBanner />;
}
